/**
 * Which apps read the tables of the documents or folders a delete dialog
 * removes. It names them and never blocks; an unknown answer says so. A
 * local workspace has no apps and shows nothing. Confirm dialogs render at
 * the app root, outside the workspace scope, so the caller hands in the
 * framework of its workspace.
 */
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import type { FrameworkProvider } from '@toeverything/infra';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

import {
  fetchDocAppResourceLifecycleImpacts,
  fetchFolderAppResourceLifecycleImpacts,
} from './app-resource-lifecycle';
import { DatabaseAppUsageList } from './database-app-usage-list';
import { createDatabaseAppUsagePort } from './database-app-usage-port';

/** A dialog lists at most this many tables. */
const MAX_TABLES = 50;

type UsedTables =
  | { status: 'loading' }
  | { status: 'unknown' }
  | {
      status: 'ready';
      tables: ReadonlyArray<{ databaseId: string; label: string }>;
    };

export function DocDeleteAppUsage(props: {
  framework: FrameworkProvider;
  target: { kind: 'doc' | 'folder'; ids: readonly string[] };
  operation: 'trash' | 'permanent_delete';
}): ReactElement | null {
  const t = useI18n();
  const { framework } = props;
  const workspace = framework.getOptional(WorkspaceService)?.workspace;
  // A local workspace has no apps and no server to ask.
  const cloudWorkspaceId =
    workspace && workspace.flavour !== 'local' ? workspace.id : null;
  const port = useMemo(
    () => createDatabaseAppUsagePort(framework),
    [framework]
  );
  const [used, setUsed] = useState<UsedTables>({ status: 'loading' });
  const { kind } = props.target;
  const idsKey = props.target.ids.join('\u0000');

  useEffect(() => {
    if (!cloudWorkspaceId || !idsKey) return;
    let stopped = false;
    const ids = idsKey.split('\u0000');
    setUsed({ status: 'loading' });
    (kind === 'doc'
      ? fetchDocAppResourceLifecycleImpacts(
          cloudWorkspaceId,
          props.operation,
          ids
        )
      : fetchFolderAppResourceLifecycleImpacts(
          cloudWorkspaceId,
          props.operation,
          ids
        )
    ).then(
      impacts => {
        if (stopped) return;
        const tables = new Map<string, string>();
        for (const impact of impacts) {
          if (impact.impact !== 'data_source_used_by_app' || !impact.databaseId)
            continue;
          if (tables.has(impact.databaseId)) continue;
          if (tables.size === MAX_TABLES) break;
          tables.set(impact.databaseId, impact.dataSourceLabel ?? '');
        }
        setUsed({
          status: 'ready',
          tables: [...tables].map(([databaseId, label]) => ({
            databaseId,
            label,
          })),
        });
      },
      () => {
        if (!stopped) setUsed({ status: 'unknown' });
      }
    );
    return () => {
      stopped = true;
    };
  }, [cloudWorkspaceId, idsKey, kind, props.operation]);

  if (!cloudWorkspaceId || !port) return null;
  if (used.status === 'unknown') {
    return (
      <p data-testid="doc-delete-app-usage-unknown">
        {t['com.streamlain.dataview.database.appUsage.unknown']()}
      </p>
    );
  }
  if (used.status === 'loading' || used.tables.length === 0) return null;
  return (
    <div data-testid="doc-delete-app-usage">
      <p>{t['com.streamlain.trash.appUsage.lead']()}</p>
      <ul>
        {used.tables.map(table => (
          <li key={table.databaseId} data-database-id={table.databaseId}>
            <span>{table.label}</span>
            <DatabaseAppUsageList databaseId={table.databaseId} port={port} />
          </li>
        ))}
      </ul>
    </div>
  );
}
